import { AccountCircle, Menu as MenuIcon } from "@mui/icons-material";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import { setUserData } from "../../lib/redux/slices/userSlice";
import CategoryNav from "./category";
import EcommerceCategories from "./EcommerCategories";
import Logo from "./Logo";
import MobileViewSearch from "./MobileViewSearch";
import NavLinks from "./NavLinks";
import "./scroll.css";
import SearchBar from "./SearchBar";
import SearchBarForEcom from "./SearchBarForEcom";
import SearchBarSmall from "./SearchBarSmall";
import SearchBarSmallForEcommerce from "./SearchbarSmallForEcom";
import LanguageMenu from "../languageMenu";
import { t } from "i18next";

const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [isScrolled, setIsScrolled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const cartData = useSelector((state) => state.cartReducer.cartData);
  const userData = useSelector((state) => state.userReducer.user);
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  const isServicesPage = ["/", "/cleaning-services", "/car-services"].includes(
    location.pathname
  );

  useEffect(() => {
    if (isServicesPage) {
      const handleScroll = () => {
        if (window.scrollY > 50) {
          setIsScrolled(true);
        } else {
          setIsScrolled(false);
        }
      };

      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [isServicesPage]);

  return (
    <>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        zIndex={"999999"}
        sx={{
          // top: "40px",
          width: "100%",
          height: isServicesPage ? (isScrolled ? "auto" : "auto") : "80px",
          backgroundColor: "#fff",
          transition: "box-shadow 0.3s ease-in-out",
          borderBottom: isServicesPage ? "none " : "1px solid #eee",
          boxShadow: isScrolled
            ? "0 3px 12px 0 rgba(0,0,0,0.1),0 1px 2px 0 rgba(0,0,0,0.08)"
            : "none",
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            px: 0,
          }}
        >
          <Toolbar
            sx={{ justifyContent: "space-between", paddingTop: 1, px: 1 }}
          >
            <Box>
              <Logo />
            </Box>

            <Box sx={{ display: { xs: "none", md: "block" }, paddingLeft: 15 }}>
              <CSSTransition
                in={isScrolled}
                timeout={400}
                classNames="search-bar"
                unmountOnExit
              >
                <div>
                  {location.pathname === "/" ? (
                    <SearchBarSmallForEcommerce />
                  ) : (
                    <SearchBarSmall />
                  )}
                </div>
              </CSSTransition>

              <CSSTransition
                in={!isScrolled}
                timeout={400}
                classNames="nav-links"
                unmountOnExit
              >
                <div>
                  <NavLinks />
                </div>
              </CSSTransition>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* <TranslateComponent /> */}

              {!isScrolled && (
                <Button
                  component="a"
                  href="https://portal.eaziday.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: { xs: "none", md: "block" },
                    background: "none",
                    borderRadius: 28,
                    "&:hover": { bgcolor: "#f5f5f5" },
                    boxShadow: "0",
                    color: "primary.main",
                  }}
                >
                  <Typography
                    fontSize="0.875rem"
                    fontFamily="Poppins"
                    padding={"2px 4px"}
                    noWrap
                  >
                    {t("becomePartner")}
                  </Typography>
                </Button>
              )}

              <IconButton onClick={() => navigate("/cart")}>
                <Badge
                  badgeContent={cartData?.length === 0 ? "0" : cartData?.length}
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "text.coralRed",
                      color: "text.white",
                    },
                  }}
                >
                  <ShoppingCartOutlinedIcon />
                </Badge>
              </IconButton>

              <LanguageMenu />

              <Box
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                  borderRadius: 28,
                  padding: "6px 14px",
                  borderColor: "grey.300",
                  boxShadow: "0",
                  color: "gray.500",
                  border: "1px solid #EBEBEB",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  "&:hover": { boxShadow: 1 },
                }}
              >
                <MenuIcon sx={{ fontSize: "20px" }} />
                {userData ? (
                  <Avatar
                    src={userData?.profileImg}
                    sx={{
                      height: 34,
                      width: 34,
                    }}
                  />
                ) : (
                  <AccountCircle sx={{ fontSize: "34px", color: "gray" }} />
                )}
              </Box>

              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                onClick={() => setAnchorEl(null)}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    boxShadow: 2,
                    borderRadius: 2,
                    width: 220,
                    "& .MuiMenuItem-root": {
                      typography: "body2",
                      py: 1,
                      px: 2.5,
                      textTransform: "capitalize",
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <Box
                  sx={{
                    display: { xs: "block", md: "none" },
                    px: 1.5,
                    paddingBottom: 1,
                    borderBottom: "1px solid #c9c7c7",
                    alignItems: "left",
                  }}
                >
                  <NavLinks typee="mobileMenu" />
                  <Button
                    component="a"
                    href="https://portal.eaziday.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontSize: "1rem",
                      background: "none",
                      borderRadius: 28,
                      boxShadow: "0",
                      color: "#757575",
                      fontWeight: 400,
                      fontFamily: "Poppins",
                      "&:hover": { bgcolor: "#f5f5f5" },
                      pl: 2,
                    }}
                  >
                    {t("becomePartner")}
                  </Button>
                </Box>
                {userData ? (
                  <>
                    <MenuItem
                      sx={{
                        color:
                          location.pathname === "/dashboard"
                            ? "primary.main"
                            : "#000",
                      }}
                      onClick={() => navigate("/dashboard")}
                    >
                      <Box display={"flex"} alignItems={"center"}>
                        <Avatar
                          src={userData?.profileImg}
                          sx={{
                            height: 34,
                            width: 34,
                          }}
                        />
                        <Typography
                          px={1}
                          fontSize={"0.875rem"}
                          fontWeight={"500"}
                          textAlign={"center"}
                          whiteSpace={"break-spaces"}
                        >
                          {`${userData?.firstname || "N/A"} ${
                            userData?.lastname || " N/A"
                          }`}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem
                      sx={{
                        color:
                          location.pathname === "/dashboard"
                            ? "primary.main"
                            : "#000",
                      }}
                      onClick={() => navigate("/dashboard")}
                    >
                      {t("dashboard")}
                    </MenuItem>
                    {/* <MenuItem
                      sx={{
                        color:
                          linknav?.href === "/pricing"
                            ? "primary.main"
                            : "#000",
                      }}
                      onClick={() => navigate("/pricing")}
                    >
                      Pricing
                    </MenuItem> */}
                    <MenuItem
                      sx={{
                        color:
                          location.pathname === "/help-center"
                            ? "primary.main"
                            : "#000",
                      }}
                      onClick={() => navigate("/help-center")}
                    >
                      {t("help")}
                    </MenuItem>

                    <Divider />
                    <MenuItem
                      onClick={() => {
                        localStorage.removeItem("user_data");
                        dispatch(setUserData(null));
                        navigate("/login");
                      }}
                    >
                      {t("logOut")}
                    </MenuItem>
                  </>
                ) : (
                  <>
                    <MenuItem
                      sx={{
                        color:
                          location.pathname === "/register"
                            ? "primary.main"
                            : "#000",
                      }}
                      onClick={() => navigate("/register")}
                    >
                      {t("signUp")}
                    </MenuItem>
                    <MenuItem
                      sx={{
                        color:
                          location.pathname === "/login"
                            ? "primary.main"
                            : "#000",
                      }}
                      onClick={() => navigate("/login")}
                    >
                      {t("logIn")}
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      sx={{
                        color:
                          location.pathname === "/pricing"
                            ? "primary.main"
                            : "#000",
                      }}
                      onClick={() => navigate("/pricing")}
                    >
                      {t("pricing")}
                    </MenuItem>
                    <MenuItem
                      sx={{
                        color:
                          location.pathname === "/help-center"
                            ? "primary.main"
                            : "#000",
                      }}
                      onClick={() => navigate("/help-center")}
                    >
                      {t("help")}
                    </MenuItem>
                  </>
                )}
              </Menu>
            </Box>
          </Toolbar>

          {isServicesPage && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                pb: 2,
                // overflowY:{xs: "auto",sm:"visible"},
              }}
            >
              {!isScrolled &&
                (location.pathname === "/" ? (
                  <SearchBarForEcom />
                ) : (
                  <>
                    <MobileViewSearch />
                    <SearchBar />
                  </>
                ))}
            </Box>
          )}
        </Container>

        {isServicesPage && (
          <Box
            pb={0}
            borderTop={1}
            borderColor={"divider"}
            display={"flex"}
            justifyContent={"center"}
          >
            {location.pathname === "/" ? (
              <EcommerceCategories />
            ) : (
              <CategoryNav />
            )}
          </Box>
        )}
      </AppBar>
      {isServicesPage && <Box marginTop={"240px"}></Box>}
    </>
  );
};

export default NavBar;
